import { memo, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { SideBar, SVGContainer } from "../PlayerDetail/Attributes/TransferChart";
import { useHorizontalScroll } from "../../utils/HorizontalScrollHook";
import { ColorAddedPlayer, IPlayerComparisonData } from "../../types/IPlayerComparison";
import { useTheme } from "../../contexts/ThemeContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { formatPlayerCode } from "./ScattrPlotUtils";
import Cross from "../../assets/icons/Cross.png";

const ChartContainer = styled.div`
  position: relative;
  min-width: 80%;
  display: flex;
  border: 1px solid ${({ theme }) => theme.playerDataBorder};
  min-height: 860px;
  border-radius: 10px;
  background-color: transparent;
  font-family: "DM Mono", monospace;
  margin-bottom: 40px;
  & .transfer-dotlabel-container {
    color: ${({ theme }) => theme.NumberColor};
  }
  .lines-group path {
    pointer-events: all;
    &:hover {
      cursor: pointer;
    }
  }
`;

const StyledUL = styled.ul`
  list-style: none;
  margin-block-end: 0px;
  padding-inline-start: 0px;
  > li {
    height: 60px;
    padding: 20px 20px 20px;
    text-align: right;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    -webkit-user-select: none;
    user-select: none;
    &:hover {
      background-color: lightgray;
    }
  }
  > div > li {
    padding: 45px 20px 4px;
    text-align: right;
    border-top: 1px solid ${({ theme }) => theme.playerDataBorder};
    margin-left: 32px;
  }
  > li:nth-child(1) {
    font-family: "Aeonik";
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    margin-left: 12px;
  }
  > div > li {
    font-weight: 500;
    font-size: 20px;
    color: ${({ theme }) => theme.color};
  }
  & .plot-filter-items {
    left: 10px;
    top: 100px;
    height: 400px;
  }
`;

interface TrackerGraphPropTypes {
  playerComparisonData: ColorAddedPlayer[];
  playerTrackerData: any[];
  selectedFilter: {
    label: string;
    value: string;
  };
  setSelectedFilter: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
    }>
  >;
  hoveredPlayer: number;
  loading: boolean;
  setHoveredPlayer: React.Dispatch<React.SetStateAction<number>>;
  setPlayerCardID: React.Dispatch<React.SetStateAction<number>>;
  playerCardID: number;
  selectedPlayers: number[];
  setSelectedPlayers: React.Dispatch<React.SetStateAction<number[]>>;
  trackerSelectedPlayer: number[];
  setTrackerSelectedPlayer: React.Dispatch<React.SetStateAction<number[]>>;
}

type PlayerValueParams =
  | "aggression"
  | "attackContribution"
  | "attackPositioning"
  | "creativity"
  | "crossing"
  | "defensiveHeaders"
  | "dribbling"
  | "finishingAerial"
  | "finishingShots"
  | "marketValue"
  | "passingAccuracy"
  | "playerId"
  | "tackling";

interface Player {
  aggression: number;
  attackContribution: number;
  attackPositioning: number;
  creativity: number;
  crossing: number;
  defensiveHeaders: number;
  dribbling: number;
  finishingAerial: number;
  finishingShots: number;
  marketValue: number;
  passingAccuracy: number;
  playerId: number;
  start: number;
  sub: number;
  tackling: number;
  wagePerWeek: number;
  color?: string;
}

interface TrackerResponse {
  date: string;
  players: Player[];
}

const PlotFilterOptions = [
  { label: "Attack contribution", value: "attackContribution" },
  { label: "Finishing - Shots", value: "finishingShots" },
  { label: "Finishing - Aerial", value: "finishingAerial" },
  { label: "Passing accuracy", value: "passingAccuracy" },
  { label: "Market Value", value: "marketValue" },
  { label: "Starts", value: "start" },
  { label: "Wages", value: "wagePerWeek" },
  { label: "Attack positioning", value: "attackPositioning" },
  { label: "Creativity", value: "creativity" },
  { label: "Dribbling", value: "dribbling" },
  { label: "Crossing", value: "crossing" },
  { label: "Tackling", value: "tackling" },
  { label: "Defensive headers", value: "defensiveHeaders" },
  { label: "Ball Recovery", value: "aggression" },
];

export const TrackerGraph: React.FC<TrackerGraphPropTypes> = memo(
  ({
    playerComparisonData,
    playerTrackerData,
    selectedFilter,
    setSelectedFilter,
    hoveredPlayer,
    loading,
    setHoveredPlayer,
    setPlayerCardID,
    playerCardID,
    selectedPlayers,
    setSelectedPlayers,
    trackerSelectedPlayer,
    setTrackerSelectedPlayer
  }) => {
    const { theme } = useTheme();
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const scrollRef = useHorizontalScroll();
    const barWidth = 88;
    const totalBarGroupWidth = playerTrackerData !== null ? (playerTrackerData?.length + 1) * barWidth : barWidth;
    const adjustedChartWidth = totalBarGroupWidth;
    const getDataExtremes = (data: TrackerResponse[]) => {
      if (!data || data.length === 0) {
        return { min: 0, max: 100 };
      }
      const allValues = data?.flatMap((entry) =>
        entry.players
          .map((player) => player[selectedFilter.value as PlayerValueParams] as number)
          .filter((value) => value !== undefined && !isNaN(value))
      );
      if (allValues.length === 0) {
        return { min: 0, max: 100 };
      }
      return {
        min: Math.floor(d3.min(allValues) || 0),
        max: Math.ceil(d3.max(allValues) || 100),
      };
    };
    const { min, max } = getDataExtremes(playerTrackerData);
    const labels = useMemo(() => {
      if (loading || !playerTrackerData?.length) return [];
      const labelCount = 10;
      return Array.from({ length: labelCount }, (_, index) => {
        const value = max - ((max - min) * index) / (labelCount - 1);
        return Math.floor(Math.round(value * 10) / 10);
      });
    }, [playerTrackerData, selectedFilter.value, loading]);
    useEffect(() => {
      const svg = d3.select(svgRef.current);
      const svgHeight = svg.node()?.getBoundingClientRect().height;
      svg.selectAll("g:not(.bar-group)").remove();
      let barGroup = svg.select("g");
      // @ts-ignore
      barGroup = svg
        .append("g")
        .attr("class", "bar-group")
        .attr("height", "100%")
        .attr("width", playerTrackerData?.length > 20 ? playerTrackerData?.length * barWidth : 88 * 40);
      const colors = [theme.dropdownBorderColor, theme.shade2Chart];
      if (playerTrackerData?.length > 20 || playerTrackerData) {
        playerTrackerData?.forEach((_, i) => {
          barGroup
            .append("rect")
            .attr("width", barWidth)
            .attr("height", "100%")
            .classed("bar", true)
            .attr("x", i * barWidth)
            .attr("y", 0)
            .style("fill", colors[i % 2])
            .style("cursor", "pointer");
        });
      } else {
        svg?.attr("width", 88 * 40);
        for (let i = 0; i < 40; i++) {
          barGroup
            .append("rect")
            .attr("width", barWidth)
            .attr("height", "100%")
            .classed("bar", true)
            .attr("x", i * barWidth)
            .attr("y", 0)
            .style("fill", colors[i % 2])
            .style("cursor", "pointer");
        }
      }
      barGroup
        .append("line")
        .attr("x1", playerTrackerData?.length * barWidth)
        .attr("y1", 0)
        .attr("x2", playerTrackerData?.length * barWidth)
        .attr("y2", "100%")
        .style("stroke", "red")
        .style("stroke-width", 4);
      barGroup
        .append("rect")
        .attr("width", barWidth)
        .attr("height", "100%")
        .classed("bar", true)
        .attr("x", playerTrackerData?.length * barWidth)
        .attr("y", 0)
        .style("fill", colors[playerTrackerData?.length % 2])
        .style("cursor", "pointer");
      const dateGroup = svg
        .append("g")
        .attr("height", "100%")
        .attr("width", playerTrackerData?.length * barWidth);
      const sortedData = playerTrackerData?.map((data) => data)?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      sortedData?.forEach((player, i) => {
        const date = new Date(player?.date);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const formattedDate = `${day}.${month}`;
        const year = date.getFullYear();
        dateGroup
          .append("text")
          .text(formattedDate)
          .attr("x", i * barWidth + barWidth / 2)
          .attr("y", 60)
          .attr("text-anchor", "middle")
          .attr("alignment-baseline", "middle")
          .style("font-size", "12px")
          .style("font-family", "DM Mono")
          .style("fill", "#111111");
        dateGroup
          .append("text")
          .text(year)
          .attr("x", i * barWidth + barWidth / 2)
          .attr("y", 75)
          .attr("text-anchor", "middle")
          .attr("alignment-baseline", "middle")
          .style("font-size", "12px")
          .style("font-family", "DM Mono")
          .style("fill", "#111111");
      });
      const xScale = d3
        .scaleLinear()
        .domain([0, sortedData?.length - 1])
        .range([0, playerTrackerData?.length * barWidth + 30 - barWidth]);
      const yScale = d3.scaleLinear().domain([min, max]).range([svgHeight!, 190]);
      const brushGroup = svg.append("g").attr("class", "brush");
      const brush = d3
        .brush()
        .extent([
          [0, 0],
          [playerTrackerData?.length * barWidth, svgHeight!],
        ])
        .on("start", function () {
          d3.select(this).attr("class", "selecting");
          clearTimeout(hoverTimeout);
          setHoveredPlayer(0);
        })
        .on("end", (event) => {
          if (!event.selection) return;
          const [[x0, y0], [x1, y1]] = event.selection;
          const selectedPaths = linesGroup.selectAll("path").filter(function () {
            const path = d3.select(this);
            const pathElement = path.node();
            if (!pathElement) return false;
            // @ts-ignore
            const bbox = pathElement.getBBox();
            const pathX = bbox.x;
            const pathY = bbox.y;
            return pathX >= x0 && pathX <= x1 && pathY >= y0 && pathY <= y1;
          });
          const selectedPlayerIds = selectedPaths.nodes().map((node) => parseInt(d3.select(node).attr("class").split("-")[2]));
          if (selectedPlayerIds.length > 0) {
            setSelectedPlayers((prevSelected) => {
              const allAlreadySelected = selectedPlayerIds.every((id) => prevSelected.includes(id));
              if (allAlreadySelected) {
                return prevSelected.filter((id) => !selectedPlayerIds.includes(id));
              } else {
                return Array.from(new Set([...prevSelected, ...selectedPlayerIds]));
              }
            });
          }
          brushGroup.call(brush.move, null);
        });
      brushGroup.call(brush);
      barGroup.on("click", (event) => {
        if (event.target === svg.node()) {
          setSelectedPlayers([]);
        }
      });
      const linesGroup = svg
        .append("g")
        .attr("class", "lines-group")
        .attr("height", "100%")
        .attr("transform", `translate(${barWidth / 2}, 0)`);
      const playersMap = new Map<number, Player[]>();
      sortedData?.forEach((data: TrackerResponse) => {
        data?.players?.forEach((player: Player) => {
          if (!playersMap.has(player.playerId)) {
            playersMap.set(player.playerId, []);
          }
          playersMap.get(player.playerId)?.push(player);
        });
      });
      let hoverTimeout: NodeJS.Timeout;
      const handleLineClick = (currentPlayer: any) => {
        if (playerCardID === currentPlayer.playerId) {
          setPlayerCardID(0);
        } else {
          setPlayerCardID(currentPlayer.playerId);
        }
      };
      sortedData?.reverse()?.forEach((dateData, dateIndex) => {
        if (dateIndex < sortedData.length - 1) {
          const nextDateData = sortedData[dateIndex + 1];
          dateData?.players?.forEach((currentPlayer: any) => {
            if (trackerSelectedPlayer?.includes(currentPlayer.playerId)) {
              const nextPlayer = nextDateData?.players?.find((p: any) => p.playerId === currentPlayer.playerId);
              if (nextPlayer) {
                linesGroup
                  .append("path")
                  .datum([currentPlayer, nextPlayer])
                  .attr("fill", "none")
                  .attr(
                    "stroke",
                    selectedPlayers?.includes(currentPlayer?.playerId)
                      ? "#4AF075"
                      : playerComparisonData?.find((playerComp) => playerComp?.playerId === currentPlayer.playerId)?.colorCode!
                  )
                  .attr("stroke-width", hoveredPlayer > 0 ? (hoveredPlayer === currentPlayer.playerId ? 8 : 1) : 4)
                  .attr(
                    "d",
                    d3
                      .line<Player>()
                      .x((_, i) => xScale(sortedData.length - 1 - (dateIndex + i)))
                      .y((d) => yScale(d[selectedFilter.value as PlayerValueParams] as number))
                  )
                  .attr("class", `player-line-${currentPlayer.playerId}`)
                  .style("cursor", "pointer")
                  .on("mouseenter", function () {
                    if (d3.select(brushGroup.node()).classed("selecting")) return;
                    hoverTimeout = setTimeout(() => {
                      setHoveredPlayer(currentPlayer.playerId);
                    }, 500);
                  })
                  .on("mouseout", function () {
                    clearTimeout(hoverTimeout);
                    setHoveredPlayer(0);
                  })
                  .on("click", () => handleLineClick(currentPlayer));
                if (dateIndex === 0) {
                  linesGroup
                    .append("rect")
                    .attr("width", 80)
                    .attr("height", 25)
                    .attr("x", xScale(sortedData.length - 1 - dateIndex) - 28)
                    .attr("y", yScale(currentPlayer[selectedFilter.value as PlayerValueParams] as number) - 12.5)
                    .attr("fill", selectedPlayers?.includes(currentPlayer?.playerId) ? "#4AF075" : "white")
                    .attr("stroke", "#DADADA")
                    .attr("class", `end-box-${currentPlayer.playerId}`)
                    .style("rx", "2")
                    .style("ry", "2");
                  linesGroup
                    .append("text")
                    .attr("x", xScale(sortedData.length - 1 - dateIndex) + 22)
                    .attr("y", yScale(currentPlayer[selectedFilter.value as PlayerValueParams] as number))
                    .attr("text-anchor", "middle")
                    .attr("dominant-baseline", "middle")
                    .attr("fill", selectedPlayers?.includes(currentPlayer?.playerId) ? "#fff" : "#000")
                    .style("font-size", "14px")
                    .style("font-family", "DM Mono")
                    .style("font-weight", "500")
                    .text(Math.round(currentPlayer[selectedFilter.value as PlayerValueParams] as number));
                  linesGroup
                    .append("text")
                    .attr("x", xScale(sortedData.length - 1 - dateIndex) - 8)
                    .attr("y", yScale(currentPlayer[selectedFilter.value as PlayerValueParams] as number))
                    .attr("text-anchor", "middle")
                    .attr("dominant-baseline", "middle")
                    .attr("fill", "#7C7D79")
                    .style("font-size", "12px")
                    .style("font-family", "DM Mono")
                    .text(
                      formatPlayerCode(
                        playerComparisonData?.find((player) => player.playerId === currentPlayer.playerId)?.playerLabel || ""
                      )
                    );
                  linesGroup
                    .append("image")
                    .attr("href", Cross)
                    .attr("x", xScale(sortedData.length - 1 - dateIndex) + 35)
                    .attr("y", yScale(currentPlayer[selectedFilter.value as PlayerValueParams] as number) - 9.5)
                    .attr("width", 16)
                    .attr("height", 16)
                    .style("cursor", "pointer")
                    .on("click", () => {
                      if (trackerSelectedPlayer?.includes(currentPlayer.playerId)) {
                        setTrackerSelectedPlayer(trackerSelectedPlayer.filter((id) => id !== currentPlayer.playerId));
                      }
                    });
                }
              }
            }
          });
        }
      });
    }, [playerTrackerData, selectedFilter.value, adjustedChartWidth, hoveredPlayer, selectedPlayers, trackerSelectedPlayer]);
    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          left: scrollRef.current.scrollWidth,
          behavior: "smooth",
        });
      }
    }, [playerTrackerData]);
    const menuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          setIsFilterMenuOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);
    return (
      <>
        <ChartContainer>
          <SideBar>
            <StyledUL>
              <li onClick={() => setIsFilterMenuOpen((prev) => !prev)}>
                {selectedFilter?.label} {isFilterMenuOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </li>
              {isFilterMenuOpen && (
                <div className="plot-filter-items" ref={menuRef}>
                  {PlotFilterOptions?.map((option) => (
                    <div
                      className="plot-filter-option"
                      style={{ backgroundColor: selectedFilter?.value === option?.value ? "#F2F5EE" : "" }}
                      onClick={() => {
                        setIsFilterMenuOpen(false);
                        setSelectedFilter(option);
                      }}
                    >
                      {" "}
                      {option?.label}
                    </div>
                  ))}
                </div>
              )}
              <div className="side-transfer-container">
                {playerTrackerData?.length > 0 ? (
                  <>
                    {labels !== undefined &&
                      labels.map((label) =>
                        playerComparisonData?.length === 0 ? (
                          <li className="side-label"></li>
                        ) : (
                          <li className="side-label" key={label}>
                            {label}
                          </li>
                        )
                      )}
                  </>
                ) : (
                  <>
                    {labels !== undefined &&
                      [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].reverse().map((label) =>
                        playerComparisonData?.length === 0 ? (
                          <li className="side-label"></li>
                        ) : (
                          <li className="side-label" key={label}>
                            {label}
                          </li>
                        )
                      )}
                  </>
                )}
              </div>
            </StyledUL>
          </SideBar>
          {/* @ts-ignore */}
          <SVGContainer ref={scrollRef} id="injuryContainer" className="injury-chart-container">
            <svg
              ref={svgRef}
              style={{
                position: "absolute",
                top: 0,
                height: "100%",
                width: playerTrackerData?.length > 20 ? adjustedChartWidth : 88 * 40,
              }}
            ></svg>
          </SVGContainer>
        </ChartContainer>
      </>
    );
  }
);
